import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, ReferenceLine, LineChart, Line
} from 'recharts';
import { 
  Grid3X3, TrendingUp, TrendingDown, Lightbulb, 
  ThumbsUp, AlertCircle, Sparkles, ArrowLeftRight, Settings,
  Eye, Calendar, Filter, ChevronDown, ChevronRight, Save, Plus, Trash2,
  CheckSquare, Square, Users, MessageSquare
} from 'lucide-react';

const HEAT_COLORS = {
  veryLow: '#ef4444',
  low: '#f97316',
  medium: '#fbbf24',
  good: '#84cc16',
  veryGood: '#22c55e',
};

const getHeatColor = (score) => {
  if (score === null || score === undefined) return '#f1f5f9';
  if (score < 4) return HEAT_COLORS.veryLow;
  if (score < 5.5) return HEAT_COLORS.low;
  if (score < 6.5) return HEAT_COLORS.medium;
  if (score < 7.5) return HEAT_COLORS.good;
  return HEAT_COLORS.veryGood;
};

const getTrendColor = (change) => {
  if (change === null || change === undefined) return '#f1f5f9';
  if (change < -1) return HEAT_COLORS.veryLow;
  if (change < -0.3) return HEAT_COLORS.low;
  if (change < 0.3) return '#94a3b8';
  if (change < 1) return HEAT_COLORS.good;
  return HEAT_COLORS.veryGood;
};

const formatScore = (score) => {
  if (score === null || score === undefined) return '-';
  return parseFloat(score).toFixed(1);
};

// Simplified Segment Highlight Tile (no scores/trends)
function SegmentHighlightTile({ title, icon: Icon, segments, variant = 'positive', onClick }) {
  const colorClass = variant === 'positive' 
    ? 'bg-emerald-50 border-emerald-200' 
    : 'bg-rose-50 border-rose-200';
  const iconColorClass = variant === 'positive' ? 'text-emerald-600' : 'text-rose-600';
  const textColorClass = variant === 'positive' ? 'text-emerald-700' : 'text-rose-700';
  
  return (
    <Card className={`${colorClass} border`}>
      <CardHeader className="pb-2">
        <CardTitle className={`text-sm font-medium flex items-center gap-2 ${textColorClass}`}>
          <Icon className={`w-4 h-4 ${iconColorClass}`} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1.5">
          {segments.slice(0, 5).map((seg, i) => (
            <div 
              key={i} 
              className="flex items-center gap-2 p-2 bg-white/60 rounded cursor-pointer hover:bg-white/80 transition-colors"
              onClick={() => onClick?.(seg)}
            >
              <span className={`text-xs font-medium ${textColorClass} w-5`}>#{i + 1}</span>
              <span className="text-sm truncate flex-1">{seg.name}</span>
              <Sparkles className="w-3.5 h-3.5 text-amber-500 opacity-60" />
            </div>
          ))}
          {segments.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No data available</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Heat Map Cell Component
function HeatMapCell({ value, change, showTrend, onClick, belowThreshold, notApplicable }) {
  // Grey out if below anonymity threshold or not applicable
  if (notApplicable) {
    return (
      <div 
        className="w-12 h-9 flex items-center justify-center text-xs font-medium rounded bg-slate-100"
        title="Question not asked to this segment"
      >
        <span className="text-slate-400">N/A</span>
      </div>
    );
  }
  
  if (belowThreshold) {
    return (
      <div 
        className="w-12 h-9 flex items-center justify-center text-xs font-medium rounded bg-slate-200"
        title="Below anonymity threshold (< 3 responses)"
      >
        <span className="text-slate-400">--</span>
      </div>
    );
  }
  
  const bgColor = showTrend ? getTrendColor(change) : getHeatColor(value);
  const displayValue = showTrend 
    ? (change !== null ? (change > 0 ? '+' : '') + change?.toFixed(1) : '-') 
    : formatScore(value);
  const textColor = value !== null || change !== null ? '#fff' : '#94a3b8';
  
  return (
    <div 
      className="w-12 h-9 flex items-center justify-center text-xs font-medium cursor-pointer hover:ring-2 hover:ring-slate-400 transition-all rounded"
      style={{ backgroundColor: bgColor, color: textColor }}
      onClick={onClick}
      title={showTrend ? `Change: ${displayValue}` : `Score: ${displayValue}`}
    >
      {displayValue}
    </div>
  );
}

// AI Insights Panel
function AIInsightsPanel({ segment, insights, onClose, loading }) {
  return (
    <Dialog open={!!segment} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            AI Insights: {segment?.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {loading ? (
            <div className="space-y-4 py-8">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#5AA2AE] border-t-transparent"></div>
                <span className="text-slate-600">Analyzing segment data and text responses...</span>
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : insights?.error ? (
            <p className="text-center text-rose-500 py-8">{insights.error}</p>
          ) : (
            <div className="space-y-6">
              {insights?.summary && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Summary</h4>
                  <p className="text-slate-600 text-sm">{insights.summary}</p>
                </div>
              )}
              
              {insights?.key_findings?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Key Findings</h4>
                  <ul className="space-y-2">
                    {insights.key_findings.map((finding, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <span>{finding}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {insights?.text_themes?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#5AA2AE]" />
                    Themes from Text Responses
                  </h4>
                  <ul className="space-y-2">
                    {insights.text_themes.map((theme, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm p-2 bg-[#5AA2AE]/5 rounded-md">
                        <span className="text-[#5AA2AE] font-medium">{i + 1}.</span>
                        <span className="text-slate-600">{theme}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {!insights?.summary && !insights?.key_findings?.length && (
                <div className="text-center py-8 text-slate-400">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No AI insights available yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Perception Gap Chart
function PerceptionGapChart({ data, onSegmentClick }) {
  const chartData = data.map(d => ({
    name: d.segment?.length > 15 ? d.segment.substring(0, 15) + '...' : d.segment,
    fullName: d.segment,
    gap: d.gap,
    selfScore: d.selfScore,
    othersScore: d.othersScore,
  })).sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));
  
  return (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ left: 100, right: 40 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" domain={[-5, 5]} tickFormatter={(v) => v > 0 ? `+${v}` : v} />
          <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11 }} />
          <RechartsTooltip 
            formatter={(value, name, props) => [
              `Gap: ${value > 0 ? '+' : ''}${value.toFixed(1)}`,
              `Self: ${props.payload.selfScore?.toFixed(1)}, Others: ${props.payload.othersScore?.toFixed(1)}`
            ]}
          />
          <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={2} />
          <Bar dataKey="gap" onClick={(data) => onSegmentClick?.(data)}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.gap >= 0 ? '#22c55e' : '#ef4444'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      <div className="flex justify-center gap-6 mt-2 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded" /> Others perceive lower
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded" /> Others perceive higher
        </span>
      </div>
    </div>
  );
}

// Perception Pairing Configuration Dialog
function PerceptionPairingDialog({ open, onClose, aboutMeQuestions, aboutOthersQuestions, pairings, onSave }) {
  const [localPairings, setLocalPairings] = useState(pairings || []);
  
  useEffect(() => {
    setLocalPairings(pairings || []);
  }, [pairings, open]);
  
  const addPairing = () => {
    setLocalPairings([...localPairings, { self_question_id: '', others_question_id: '' }]);
  };
  
  const removePairing = (index) => {
    setLocalPairings(localPairings.filter((_, i) => i !== index));
  };
  
  const updatePairing = (index, field, value) => {
    const updated = [...localPairings];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-fill names
    if (field === 'self_question_id') {
      const q = aboutMeQuestions.find(q => q.id === value);
      updated[index].self_question_name = q?.heading || '';
    } else if (field === 'others_question_id') {
      const q = aboutOthersQuestions.find(q => q.id === value);
      updated[index].others_question_name = q?.heading || '';
    }
    
    setLocalPairings(updated);
  };
  
  const handleSave = () => {
    onSave(localPairings.filter(p => p.self_question_id && p.others_question_id));
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configure Perception Gap Pairings
          </DialogTitle>
          <DialogDescription>
            Match "About Me" questions with corresponding "About Others" questions to compare self-perception with external perception.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {localPairings.map((pairing, index) => (
            <div key={index} className="flex items-start gap-3 p-3 border rounded-lg bg-slate-50">
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-xs text-slate-500">Self-Reported (About Me)</Label>
                  <Select
                    value={pairing.self_question_id}
                    onValueChange={(v) => updatePairing(index, 'self_question_id', v)}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select question..." />
                    </SelectTrigger>
                    <SelectContent>
                      {aboutMeQuestions.map(q => (
                        <SelectItem key={q.id} value={q.id}>{q.heading}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="pt-6">
                <ArrowLeftRight className="w-5 h-5 text-slate-400" />
              </div>
              
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-xs text-slate-500">External Perception (About Others)</Label>
                  <Select
                    value={pairing.others_question_id}
                    onValueChange={(v) => updatePairing(index, 'others_question_id', v)}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select question..." />
                    </SelectTrigger>
                    <SelectContent>
                      {aboutOthersQuestions.map(q => (
                        <SelectItem key={q.id} value={q.id}>{q.heading}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="mt-6 text-rose-500 hover:text-rose-600"
                onClick={() => removePairing(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          
          <Button variant="outline" onClick={addPairing} className="w-full gap-2">
            <Plus className="w-4 h-4" />
            Add Pairing
          </Button>
        </div>
        
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="gap-2">
            <Save className="w-4 h-4" />
            Save Pairings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Local storage keys
const STORAGE_KEYS = {
  selectedQuestions: 'pulse_segment_analysis_questions',
  selectedSegments: 'pulse_segment_analysis_segments',
  expandedSegmentTypes: 'pulse_segment_analysis_expanded_types',
};

// Helper to create uncategorized pseudo-segment ID
const getUncategorizedId = (segmentTypeId) => `uncategorized_${segmentTypeId}`;

export default function SegmentAnalysisPage() {
  const { projectId } = useParams();
  const { currentProject } = useProject();
  
  const [loading, setLoading] = useState(true);
  const [baseDataLoaded, setBaseDataLoaded] = useState(false); // Track when segment types are loaded
  const [activeTab, setActiveTab] = useState('heatmap');
  const [viewMode, setViewMode] = useState('values');
  const [timePeriod, setTimePeriod] = useState('30d');
  
  // Data states
  const [segmentTypes, setSegmentTypes] = useState([]);
  const [segments, setSegments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [heatMapData, setHeatMapData] = useState({});
  const [segmentCounts, setSegmentCounts] = useState({});
  const [topSegments, setTopSegments] = useState([]);
  const [bottomSegments, setBottomSegments] = useState([]);
  const [perceptionGapData, setPerceptionGapData] = useState([]);
  const [perceptionPairings, setPerceptionPairings] = useState([]);
  const [aboutOthersQuestions, setAboutOthersQuestions] = useState([]);
  
  // Filter states (with persistence)
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.selectedQuestions);
    return saved ? JSON.parse(saved) : null; // null means "all"
  });
  const [selectedSegmentIds, setSelectedSegmentIds] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.selectedSegments);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Treat empty array as "all" (null) to avoid broken state
    return (Array.isArray(parsed) && parsed.length === 0) ? null : parsed;
  });
  // Pending filter state - changes here don't trigger API until applied
  const [pendingSegmentIds, setPendingSegmentIds] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  
  const [expandedTypes, setExpandedTypes] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.expandedSegmentTypes);
    return saved ? JSON.parse(saved) : {};
  });
  
  // UI states
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showPairingConfig, setShowPairingConfig] = useState(false);
  
  // Persist filter selections
  useEffect(() => {
    if (selectedQuestionIds !== null) {
      localStorage.setItem(STORAGE_KEYS.selectedQuestions, JSON.stringify(selectedQuestionIds));
    } else {
      localStorage.removeItem(STORAGE_KEYS.selectedQuestions);
    }
  }, [selectedQuestionIds]);
  
  useEffect(() => {
    if (selectedSegmentIds !== null) {
      localStorage.setItem(STORAGE_KEYS.selectedSegments, JSON.stringify(selectedSegmentIds));
    } else {
      localStorage.removeItem(STORAGE_KEYS.selectedSegments);
    }
  }, [selectedSegmentIds]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.expandedSegmentTypes, JSON.stringify(expandedTypes));
  }, [expandedTypes]);
  
  // Fetch segment types and questions (static data)
  useEffect(() => {
    const fetchBaseData = async () => {
      try {
        const [segmentsRes, questionsRes, baseAnalysisRes, permsRes] = await Promise.all([
          api.get(`/projects/${projectId}/segments-with-types`),
          api.get(`/projects/${projectId}/questions`),
          api.get(`/projects/${projectId}/segment-heatmap-analysis?period=30d`),
          api.get(`/projects/${projectId}/user-permissions`).catch(() => ({ data: { allowed_segments: null } }))
        ]);
        
        const allowedSegs = permsRes.data?.allowed_segments;
        const allowedSet = allowedSegs ? new Set(allowedSegs) : null;
        
        // Handle segment types - API returns array directly
        const segData = segmentsRes.data || [];
        const segTypesArray = Array.isArray(segData) ? segData : (segData.segment_types || []);
        
        // Filter segments by permissions
        const filteredSegTypes = allowedSet
          ? segTypesArray.map(st => ({
              ...st,
              segments: (st.segments || []).filter(s => allowedSet.has(s.id))
            })).filter(st => st.segments.length > 0)
          : segTypesArray;
        setSegmentTypes(filteredSegTypes);
        
        // Flatten segments with type info
        const allSegments = [];
        filteredSegTypes.forEach(st => {
          (st.segments || []).forEach(s => {
            allSegments.push({ ...s, segment_type_name: st.name, segment_type_id: st.id });
          });
        });
        setSegments(allSegments);
        
        // Store ORIGINAL uncategorized counts (never changes with filter)
        const baseAnalysis = baseAnalysisRes.data || {};
        setOriginalUncategorizedCounts(baseAnalysis.uncategorized_counts || {});
        
        // Filter About Me slider questions
        const aboutMeQuestions = questionsRes.data.filter(q => 
          q.question_context === 'about_me' && 
          (q.question_type === 'slider' || q.question_type === 'scale')
        );
        setQuestions(aboutMeQuestions);
        
        // Get About Others questions for perception gap
        const othersQuestions = questionsRes.data.filter(q => 
          q.question_context === 'about_others' && 
          (q.question_type === 'slider' || q.question_type === 'scale')
        );
        setAboutOthersQuestions(othersQuestions);
        setBaseDataLoaded(true); // Mark base data as loaded
      } catch (err) {
        console.error('Failed to fetch base data:', err);
      }
    };
    
    fetchBaseData();
  }, [projectId]);
  
  // State for uncategorized data
  const [uncategorizedCounts, setUncategorizedCounts] = useState({});
  const [uncategorizedHeatmap, setUncategorizedHeatmap] = useState({});
  // Keep original uncategorized counts for filter UI (doesn't change when filter applied)
  const [originalUncategorizedCounts, setOriginalUncategorizedCounts] = useState({});
  
  // Fetch analysis data (depends on time period AND segment filter)
  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        
        // Build query params - include segment filter for intersection calculation
        let queryParams = `period=${timePeriod}`;
        if (selectedSegmentIds && selectedSegmentIds.length > 0) {
          queryParams += `&selected_segments=${selectedSegmentIds.join(',')}`;
        }
        
        const analysisRes = await api.get(`/projects/${projectId}/segment-heatmap-analysis?${queryParams}`);
        
        // Process analysis data
        const analysis = analysisRes.data || {};
        console.log('Segment analysis data:', {
          uncategorized_counts: analysis.uncategorized_counts,
          segment_counts_keys: Object.keys(analysis.segment_counts || {}).length
        });
        setHeatMapData(analysis.heatmap || {});
        setSegmentCounts(analysis.segment_counts || {});
        setUncategorizedCounts(analysis.uncategorized_counts || {});
        setUncategorizedHeatmap(analysis.uncategorized_heatmap || {});
        setTopSegments(analysis.top_segments || []);
        setBottomSegments(analysis.attention_needed || []);
        setPerceptionGapData(analysis.perception_gaps || []);
        setPerceptionPairings(analysis.perception_pairings || []);
        
      } catch (err) {
        console.error('Failed to fetch segment analysis:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalysis();
  }, [projectId, timePeriod, selectedSegmentIds]);  // Re-fetch when segment filter changes
  
  // Fetch AI insights
  const fetchAiInsights = useCallback(async (segment) => {
    setSelectedSegment(segment);
    setAiLoading(true);
    setAiInsights(null);
    
    try {
      // Include current filter context in the AI insights request
      let url = `/projects/${projectId}/segments/${segment.id}/ai-insights`;
      if (selectedSegmentIds && selectedSegmentIds.length > 0) {
        url += `?filter_segments=${selectedSegmentIds.join(',')}`;
      }
      const res = await api.get(url);
      setAiInsights(res.data);
    } catch (err) {
      console.error('Failed to fetch AI insights:', err);
      setAiInsights({ error: 'Failed to load insights' });
    } finally {
      setAiLoading(false);
    }
  }, [projectId, selectedSegmentIds]);
  
  // Save perception pairings
  const savePerceptionPairings = async (pairings) => {
    try {
      await api.post(`/projects/${projectId}/perception-pairings`, { pairings });
      setPerceptionPairings(pairings);
      // Refetch to get updated perception gaps
      const res = await api.get(`/projects/${projectId}/segment-heatmap-analysis?period=${timePeriod}`);
      setPerceptionGapData(res.data?.perception_gaps || []);
    } catch (err) {
      console.error('Failed to save pairings:', err);
    }
  };
  
  // Filtered data based on selections
  const filteredQuestions = useMemo(() => {
    if (!selectedQuestionIds || selectedQuestionIds.length === 0) return questions;
    return questions.filter(q => selectedQuestionIds.includes(q.id));
  }, [questions, selectedQuestionIds]);
  
  // Check if a segment type has any segments selected (excluding uncategorized)
  const hasSelectedSegmentsForType = useCallback((typeId) => {
    if (!selectedSegmentIds || selectedSegmentIds.length === 0) return true; // All selected
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    return typeSegments.some(s => selectedSegmentIds.includes(s.id));
  }, [selectedSegmentIds, segmentTypes]);
  
  // Check if uncategorized is selected for a segment type
  const isUncategorizedSelected = useCallback((typeId) => {
    const uncatId = getUncategorizedId(typeId);
    if (!selectedSegmentIds || selectedSegmentIds.length === 0) return true; // All selected
    return selectedSegmentIds.includes(uncatId);
  }, [selectedSegmentIds]);
  
  const filteredSegmentTypes = useMemo(() => {
    // If no filter applied (null) OR empty array (nothing explicitly selected), show all segments
    if (selectedSegmentIds === null || selectedSegmentIds.length === 0) {
      return segmentTypes.map(st => ({
        ...st,
        showUncategorized: true
      }));
    }
    
    // Check if ALL regular segments are selected (treat as no filter)
    const allSegmentIds = segments.map(s => s.id);
    const allRegularSegmentsSelected = allSegmentIds.every(id => selectedSegmentIds.includes(id));
    
    if (allRegularSegmentsSelected) {
      return segmentTypes.map(st => ({
        ...st,
        showUncategorized: true
      }));
    }
    
    // When filter IS applied, only show selected segments within each type
    // Hide entire segment type if no segments from that type are selected
    return segmentTypes
      .map(st => {
        const uncatId = getUncategorizedId(st.id);
        const isUncatSelected = selectedSegmentIds.includes(uncatId);
        const filteredSegs = (st.segments || []).filter(seg => selectedSegmentIds.includes(seg.id));
        
        // Skip types where nothing is selected
        if (filteredSegs.length === 0 && !isUncatSelected) return null;
        
        return {
          ...st,
          segments: filteredSegs,
          showUncategorized: isUncatSelected
        };
      })
      .filter(Boolean);
  }, [segmentTypes, selectedSegmentIds, segments]);
  
  // Toggle functions for filters
  const toggleQuestion = (qId) => {
    if (!selectedQuestionIds) {
      // First deselection: select all except this one
      setSelectedQuestionIds(questions.filter(q => q.id !== qId).map(q => q.id));
    } else if (selectedQuestionIds.includes(qId)) {
      const newIds = selectedQuestionIds.filter(id => id !== qId);
      setSelectedQuestionIds(newIds.length === 0 ? null : newIds);
    } else {
      setSelectedQuestionIds([...selectedQuestionIds, qId]);
    }
  };
  
  // Get current working state (pending if filter open, otherwise actual)
  const workingSegmentIds = filterOpen ? pendingSegmentIds : selectedSegmentIds;
  
  // Toggle segment in PENDING state (doesn't trigger API)
  const toggleSegment = (segId) => {
    const current = workingSegmentIds;
    if (current === null) {
      // All selected, deselect this one - keep all other segments AND uncategorized IDs
      const allSegmentIds = segments.map(s => s.id);
      const allUncatIds = segmentTypes
        .filter(st => (originalUncategorizedCounts[st.id] || 0) > 0)
        .map(st => getUncategorizedId(st.id));
      const allIds = [...allSegmentIds, ...allUncatIds];
      setPendingSegmentIds(allIds.filter(id => id !== segId));
    } else if (current.includes(segId)) {
      const newIds = current.filter(id => id !== segId);
      setPendingSegmentIds(newIds.length === 0 ? null : newIds);
    } else {
      setPendingSegmentIds([...current, segId]);
    }
  };
  
  // Toggle all segments in a type (PENDING state) - includes Uncategorized
  const toggleSegmentType = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const typeSegmentIds = typeSegments.map(s => s.id);
    const uncatId = getUncategorizedId(typeId);
    const hasUncategorized = (originalUncategorizedCounts[typeId] || 0) > 0;
    
    // Add uncategorized ID to the list if it exists
    const allTypeIds = hasUncategorized ? [...typeSegmentIds, uncatId] : typeSegmentIds;
    
    const current = workingSegmentIds;
    
    // Check if all segments in type are currently selected (including uncategorized)
    const allSelected = current === null || allTypeIds.every(id => current.includes(id));
    
    if (allSelected) {
      // Deselect all in this type
      if (current === null) {
        // Currently all selected, deselect this type - need to get all OTHER segments
        const otherSegmentIds = segments.filter(s => !typeSegmentIds.includes(s.id)).map(s => s.id);
        // Also include uncategorized IDs from other types
        const otherUncatIds = segmentTypes
          .filter(st => st.id !== typeId && (originalUncategorizedCounts[st.id] || 0) > 0)
          .map(st => getUncategorizedId(st.id));
        setPendingSegmentIds([...otherSegmentIds, ...otherUncatIds]);
      } else {
        const newIds = current.filter(id => !allTypeIds.includes(id));
        setPendingSegmentIds(newIds.length === 0 ? null : newIds);
      }
    } else {
      // Select all in this type
      if (current === null) {
        return; // All already selected
      } else {
        const newIds = [...new Set([...current, ...allTypeIds])];
        setPendingSegmentIds(newIds);
      }
    }
  };
  
  // Select/Deselect all segments (PENDING state) - includes all uncategorized
  const selectAllSegments = () => {
    setPendingSegmentIds(null);
  };
  
  const deselectAllSegments = () => {
    setPendingSegmentIds([]);
  };
  
  // Apply filter - commit pending changes to actual state
  // If empty array, treat as "select all" (null) to avoid confusing state
  const applySegmentFilter = () => {
    const filterValue = (pendingSegmentIds && pendingSegmentIds.length === 0) ? null : pendingSegmentIds;
    setSelectedSegmentIds(filterValue);
    setFilterOpen(false);
  };
  
  // Cancel filter - discard pending changes
  const cancelSegmentFilter = () => {
    setPendingSegmentIds(selectedSegmentIds);
    setFilterOpen(false);
  };
  
  // When filter opens, initialize pending state and expand all types
  const handleFilterOpenChange = (open) => {
    if (open) {
      setPendingSegmentIds(selectedSegmentIds);
      // Auto-expand all segment types for easier selection
      const allExpanded = {};
      segmentTypes.forEach(st => { allExpanded[st.id] = true; });
      setExpandedTypes(allExpanded);
    }
    setFilterOpen(open);
  };
  
  // Toggle expand/collapse for segment type in filter
  const toggleExpandType = (typeId) => {
    setExpandedTypes(prev => ({
      ...prev,
      [typeId]: !prev[typeId]
    }));
  };
  
  // Check if all segments in a type are selected (using working state) - includes Uncategorized
  // When all regular segments are selected and we're not in explicit filter mode, treat uncategorized as selected too
  const isTypeAllSelected = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const uncatId = getUncategorizedId(typeId);
    const hasUncategorized = (originalUncategorizedCounts[typeId] || 0) > 0;
    const allSegmentsSelected = typeSegments.every(s => isPendingSegmentSelected(s.id));
    
    // If all regular segments are selected and we're in "all selected" mode (null), uncategorized is implicitly selected
    if (allSegmentsSelected && workingSegmentIds === null) {
      return true;
    }
    
    const uncatSelected = hasUncategorized ? isPendingSegmentSelected(uncatId) : true;
    return allSegmentsSelected && uncatSelected;
  };
  
  // Check if some segments in a type are selected (partial)
  const isTypePartialSelected = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const uncatId = getUncategorizedId(typeId);
    const hasUncategorized = (originalUncategorizedCounts[typeId] || 0) > 0;
    
    const selectedCount = typeSegments.filter(s => isPendingSegmentSelected(s.id)).length;
    // In null mode (all selected), uncategorized is implicitly selected
    const uncatSelected = hasUncategorized && (workingSegmentIds === null || isPendingSegmentSelected(uncatId));
    const totalItems = typeSegments.length + (hasUncategorized ? 1 : 0);
    const totalSelected = selectedCount + (uncatSelected ? 1 : 0);
    
    return totalSelected > 0 && totalSelected < totalItems;
  };
  
  // Get count of selected items in a type (segments + uncategorized if applicable)
  const getTypeSelectedCount = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const uncatId = getUncategorizedId(typeId);
    const hasUncategorized = (originalUncategorizedCounts[typeId] || 0) > 0;
    
    let count = typeSegments.filter(s => isPendingSegmentSelected(s.id)).length;
    
    // If all regular segments selected and we're in null mode, include uncategorized in count
    if (hasUncategorized) {
      if (workingSegmentIds === null || isPendingSegmentSelected(uncatId)) {
        count += 1;
      }
    }
    return count;
  };
  
  // Get total items in a type (segments + uncategorized if applicable)
  const getTypeTotalCount = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const hasUncategorized = (originalUncategorizedCounts[typeId] || 0) > 0;
    return typeSegments.length + (hasUncategorized ? 1 : 0);
  };
  
  const isQuestionSelected = (qId) => !selectedQuestionIds || selectedQuestionIds.includes(qId);
  const isSegmentSelected = (segId) => !selectedSegmentIds || selectedSegmentIds.includes(segId);
  // For filter UI - use pending state
  // Special handling for uncategorized: if all regular segments are selected and workingSegmentIds is an array,
  // treat uncategorized as implicitly selected (this handles old localStorage data without uncategorized IDs)
  const isPendingSegmentSelected = (segId) => {
    if (workingSegmentIds === null) return true;
    if (workingSegmentIds.includes(segId)) return true;
    
    // Check if this is an uncategorized ID and all regular segments are selected
    if (segId.startsWith('uncategorized_')) {
      const typeId = segId.replace('uncategorized_', '');
      const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
      const allRegularSelected = typeSegments.every(s => workingSegmentIds.includes(s.id));
      // Also check that we have ALL segments from ALL types selected (meaning no active filter)
      const allSegmentIds = segments.map(s => s.id);
      const allSegmentsSelected = allSegmentIds.every(id => workingSegmentIds.includes(id));
      return allRegularSelected && allSegmentsSelected;
    }
    
    return false;
  };

  // Show loading skeleton until both base data (segment types) AND analysis data are loaded
  if (loading || !baseDataLoaded) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Segment Analysis
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Deep dive into segment performance and perception gaps
        </p>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="heatmap" className="flex items-center gap-2">
            <Grid3X3 className="w-4 h-4" />
            Heat Map
          </TabsTrigger>
          <TabsTrigger value="perception" className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4" />
            Perception Gap
          </TabsTrigger>
        </TabsList>
        
        {/* Heat Map Tab */}
        <TabsContent value="heatmap" className="space-y-6 mt-6">
          {/* Top/Bottom Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SegmentHighlightTile
              title="Most Positive Segments"
              icon={ThumbsUp}
              segments={topSegments}
              variant="positive"
              onClick={fetchAiInsights}
            />
            <SegmentHighlightTile
              title="Segments Needing Attention"
              icon={AlertCircle}
              segments={bottomSegments}
              variant="negative"
              onClick={fetchAiInsights}
            />
          </div>
          
          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant={viewMode === 'values' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('values')}
                className="gap-1.5"
              >
                <Eye className="w-4 h-4" />
                Values
              </Button>
              <Button
                variant={viewMode === 'trends' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('trends')}
                className="gap-1.5"
              >
                <TrendingUp className="w-4 h-4" />
                Trends
              </Button>
              
              {viewMode === 'trends' && (
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-36 h-9">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Filter Dropdowns */}
            <div className="flex items-center gap-2">
              {/* Enhanced Segment Filter with Tree-style */}
              <Popover open={filterOpen} onOpenChange={handleFilterOpenChange} modal={false}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Filter className="w-4 h-4" />
                    Segments
                    {selectedSegmentIds && selectedSegmentIds.length > 0 && (
                      <Badge variant="secondary" className="ml-1 px-1.5 py-0">{selectedSegmentIds.length}</Badge>
                    )}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 p-0" onInteractOutside={(e) => e.preventDefault()}>
                  {/* Header with Select All / Clear */}
                  <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
                    <span className="text-xs font-medium text-slate-600">Filter Segments</span>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs"
                        onClick={(e) => { e.stopPropagation(); selectAllSegments(); }}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-slate-500"
                        onClick={(e) => { e.stopPropagation(); deselectAllSegments(); }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  {/* Tree-style segment types */}
                  <div className="max-h-64 overflow-y-auto py-1">
                    {segmentTypes.map(st => {
                      const uncatId = getUncategorizedId(st.id);
                      const uncatCount = originalUncategorizedCounts[st.id] || 0;
                      const hasUncategorized = uncatCount > 0;
                      
                      return (
                      <div key={st.id} className="border-b border-slate-100 last:border-0">
                        {/* Segment Type Header (collapsible) */}
                        <div 
                          className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); toggleExpandType(st.id); }}
                        >
                          {expandedTypes[st.id] ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                          <Checkbox
                            checked={isTypeAllSelected(st.id)}
                            onCheckedChange={(e) => {
                              e?.stopPropagation?.();
                              toggleSegmentType(st.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={isTypePartialSelected(st.id) ? 'data-[state=checked]:bg-slate-400' : ''}
                          />
                          <span className="text-sm font-medium flex-1">{st.name}</span>
                          <Badge variant="outline" className="text-xs px-1.5">
                            {getTypeSelectedCount(st.id)}/{getTypeTotalCount(st.id)}
                          </Badge>
                        </div>
                        
                        {/* Segments (expandable) */}
                        {expandedTypes[st.id] && (
                          <div className="pl-8 pb-1">
                            {(st.segments || []).map(seg => (
                              <div 
                                key={seg.id}
                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); toggleSegment(seg.id); }}
                              >
                                <Checkbox
                                  checked={isPendingSegmentSelected(seg.id)}
                                  onCheckedChange={() => toggleSegment(seg.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-sm flex-1">{seg.name}</span>
                                <span className="text-xs text-slate-400">
                                  <Users className="w-3 h-3 inline mr-0.5" />
                                  {segmentCounts[seg.id] || 0}
                                </span>
                              </div>
                            ))}
                            
                            {/* Uncategorized option */}
                            {hasUncategorized && (
                              <div 
                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer bg-slate-50/50"
                                onClick={(e) => { e.stopPropagation(); toggleSegment(uncatId); }}
                              >
                                <Checkbox
                                  checked={isPendingSegmentSelected(uncatId)}
                                  onCheckedChange={() => toggleSegment(uncatId)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-sm flex-1 italic text-slate-500">Uncategorized</span>
                                <span className="text-xs text-slate-400">
                                  <Users className="w-3 h-3 inline mr-0.5" />
                                  {uncatCount}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );})}
                  </div>
                  
                  {/* Apply / Cancel Footer */}
                  <div className="flex items-center justify-end gap-2 px-3 py-2 border-t bg-slate-50">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 px-3 text-xs"
                      onClick={(e) => { e.stopPropagation(); cancelSegmentFilter(); }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      className="h-7 px-3 text-xs bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                      onClick={(e) => { e.stopPropagation(); applySegmentFilter(); }}
                    >
                      Apply Filter
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Question Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Filter className="w-4 h-4" />
                    Questions
                    {selectedQuestionIds && <Badge variant="secondary" className="ml-1 px-1.5 py-0">{selectedQuestionIds.length}</Badge>}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
                  {questions.map(q => (
                    <DropdownMenuCheckboxItem
                      key={q.id}
                      checked={isQuestionSelected(q.id)}
                      onCheckedChange={() => toggleQuestion(q.id)}
                    >
                      <span className="truncate">{q.heading}</span>
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {/* Heat Map */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-[#5AA2AE]" />
                {viewMode === 'values' ? 'Segment Scores' : 'Score Changes'}
              </CardTitle>
              <CardDescription>
                {viewMode === 'values' 
                  ? 'Average scores by segment and question'
                  : `Change over ${timePeriod === '7d' ? '7 days' : timePeriod === '30d' ? '30 days' : '90 days'}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-2 min-w-[160px] sticky left-0 bg-white z-10">
                        <span className="text-xs font-medium text-slate-500">Segment</span>
                      </th>
                      {filteredQuestions.map(q => (
                        <th key={q.id} className="p-1 min-w-[50px]">
                          <div 
                            className="writing-mode-vertical text-xs font-medium text-slate-500 h-24 flex items-start justify-center pt-1"
                            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                            title={q.heading}
                          >
                            {q.heading?.substring(0, 20)}{q.heading?.length > 20 ? '...' : ''}
                          </div>
                        </th>
                      ))}
                      <th className="w-10 sticky right-0 bg-white align-bottom pb-1">
                        <span className="text-xs text-slate-400">AI</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSegmentTypes.map(st => (
                      <React.Fragment key={st.id}>
                        {/* Segment Type Header */}
                        <tr className="bg-slate-100">
                          <td colSpan={filteredQuestions.length + 2} className="p-2 sticky left-0">
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                              {st.name}
                            </span>
                          </td>
                        </tr>
                        {/* Segments */}
                        {(st.segments || []).map(seg => (
                          <tr key={seg.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="p-2 sticky left-0 bg-white">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{seg.name}</span>
                                <Badge variant="outline" className="text-xs px-1 py-0 h-5 font-normal text-slate-500">
                                  <Users className="w-3 h-3 mr-0.5" />
                                  {segmentCounts[seg.id] || 0}
                                </Badge>
                              </div>
                            </td>
                            {filteredQuestions.map(q => {
                              const cellData = heatMapData[seg.id]?.[q.id] || {};
                              return (
                                <td key={q.id} className="p-0.5 text-center">
                                  <HeatMapCell
                                    value={cellData.score}
                                    change={cellData.change}
                                    showTrend={viewMode === 'trends'}
                                    belowThreshold={cellData.below_threshold && cellData.response_count > 0}
                                    notApplicable={!cellData.applicable && cellData.response_count === 0}
                                  />
                                </td>
                              );
                            })}
                            <td className="p-0.5 sticky right-0 bg-white">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => fetchAiInsights(seg)}
                                data-testid={`ai-insights-btn-${seg.id}`}
                              >
                                <Sparkles className="w-4 h-4 text-amber-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Uncategorized Row for this Segment Type - show based on filter */}
                        {st.showUncategorized && (uncategorizedCounts[st.id] > 0 || originalUncategorizedCounts[st.id] > 0) && (
                          <tr className="border-b border-slate-100 hover:bg-slate-50/50 bg-slate-50/30">
                            <td className="p-2 sticky left-0 bg-slate-50/30">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium italic text-slate-500">Uncategorized</span>
                                <Badge variant="outline" className="text-xs px-1 py-0 h-5 font-normal text-slate-400">
                                  <Users className="w-3 h-3 mr-0.5" />
                                  {uncategorizedCounts[st.id] ?? 0}
                                </Badge>
                              </div>
                            </td>
                            {filteredQuestions.map(q => {
                              const cellData = uncategorizedHeatmap[st.id]?.[q.id] || {};
                              return (
                                <td key={q.id} className="p-0.5 text-center">
                                  <HeatMapCell
                                    value={cellData.score}
                                    change={cellData.change}
                                    showTrend={viewMode === 'trends'}
                                    belowThreshold={cellData.below_threshold && cellData.response_count > 0}
                                    notApplicable={!cellData.applicable && cellData.response_count === 0}
                                  />
                                </td>
                              );
                            })}
                            <td className="p-0.5 sticky right-0 bg-slate-50/30">
                              {/* No AI for uncategorized */}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                
                {filteredSegmentTypes.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <Grid3X3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No segments to display</p>
                    <p className="text-sm mt-1">Adjust your segment filter to see data</p>
                  </div>
                )}
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
                <span className="text-xs text-slate-500">{viewMode === 'values' ? 'Score:' : 'Change:'}</span>
                {viewMode === 'values' ? (
                  <>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.veryLow }} /><span className="text-xs">&lt;4</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.low }} /><span className="text-xs">4-5.5</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.medium }} /><span className="text-xs">5.5-6.5</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.good }} /><span className="text-xs">6.5-7.5</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.veryGood }} /><span className="text-xs">&gt;7.5</span></div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.veryLow }} /><span className="text-xs">&lt;-1</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: '#94a3b8' }} /><span className="text-xs">±0.3</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: HEAT_COLORS.veryGood }} /><span className="text-xs">&gt;+1</span></div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Perception Gap Tab */}
        <TabsContent value="perception" className="space-y-6 mt-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Perception Gap Analysis</p>
                    <p className="text-sm text-blue-700">
                      Compare self-reported scores (About Me) with external perception (About Others).
                      {perceptionPairings.length === 0 && ' Configure question pairings to enable this view.'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPairingConfig(true)} className="shrink-0">
                  <Settings className="w-4 h-4 mr-1" />
                  Configure
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {perceptionPairings.length > 0 && perceptionGapData.length > 0 ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowLeftRight className="w-5 h-5 text-[#5AA2AE]" />
                    Self vs Others Perception
                  </CardTitle>
                  <CardDescription>
                    Positive = others perceive higher than self, Negative = others perceive lower
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PerceptionGapChart 
                    data={perceptionGapData} 
                    onSegmentClick={(data) => {
                      const seg = segments.find(s => s.name === data.fullName);
                      if (seg) fetchAiInsights(seg);
                    }}
                  />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Active Question Pairings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {perceptionPairings.map((pair, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {pair.self_question_name} ↔ {pair.others_question_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : perceptionPairings.length > 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ArrowLeftRight className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <p className="text-slate-700 font-medium">Pairings configured - awaiting responses</p>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  The Perception Gap visualization requires "About Others" survey responses. 
                  Stakeholders need to complete the "About Others" questions rating other stakeholders 
                  before gap data can be calculated.
                </p>
                <div className="mt-4 p-3 bg-slate-50 rounded-lg inline-block">
                  <p className="text-xs text-slate-600 font-medium mb-2">Configured Pairings:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {perceptionPairings.map((pair, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {pair.self_question_name} ↔ {pair.others_question_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <ArrowLeftRight className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No perception gap data available</p>
                <p className="text-sm text-slate-400 mt-1">
                  Configure question pairings to compare self vs external perception
                </p>
                <Button variant="outline" className="mt-4" onClick={() => setShowPairingConfig(true)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Pairings
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* AI Insights Panel */}
      <AIInsightsPanel
        segment={selectedSegment}
        insights={aiInsights}
        onClose={() => setSelectedSegment(null)}
        loading={aiLoading}
      />
      
      {/* Perception Pairing Config */}
      <PerceptionPairingDialog
        open={showPairingConfig}
        onClose={() => setShowPairingConfig(false)}
        aboutMeQuestions={questions}
        aboutOthersQuestions={aboutOthersQuestions}
        pairings={perceptionPairings}
        onSave={savePerceptionPairings}
      />
    </div>
  );
}
